import React from 'react';
import { ShoppingCart, Trash2, Share2, CheckSquare, Square, Edit, Save, XCircle } from 'lucide-react';
import { updateCartItem, type CartItem } from '../../services/cartService';
import type { ShoppingCartData } from './shoppingCartTypes';
import type { CartSelectionTotals, VumPromoSlice } from './checkoutFormTotals';
import { getMyCartItemDetails } from './myCartSidebarItemDetails';
import { cartCatalogUnitPriceTaxIncl } from './cartCatalogDisplayPrice';
import {
  clientCatalogGoodsLineTaxIncl,
  clientCatalogStayLineTaxIncl,
  clientCatalogPriceStayPerNight,
} from '../../lib/quebecSalesTax';
import { useWebDesignedCart } from '../../contexts/WebDesignedCartContext';

type TFn = (key: string) => string;

export interface MyCartSidebarLineItemsProps {
  t: TFn;
  cartItems: CartItem[];
  selectedSection: 'all' | 'liked' | 'favorites';
  selectedItems: Set<string>;
  shoppingCartData?: import('./shoppingCartTypes').ShoppingCartData | null;
  calculatedTotals: CartSelectionTotals;
  vumPromo?: VumPromoSlice | null;
  editingItemId: string | null;
  setEditingItemId: React.Dispatch<React.SetStateAction<string | null>>;
  editQuantity: number;
  setEditQuantity: React.Dispatch<React.SetStateAction<number>>;
  handleToggleSelect: (itemId: string) => void;
  handleOpenItemDetails: (item: CartItem) => void;
  handleRemoveFromCart: (itemId: string) => void;
  handleToggleShare: (itemId: string) => void;
}

const MyCartSidebarLineItems: React.FC<MyCartSidebarLineItemsProps> = (props) => {
  const {
    t,
    cartItems,
    selectedSection,
    selectedItems,
    shoppingCartData,
    calculatedTotals,
    vumPromo,
    editingItemId,
    setEditingItemId,
    editQuantity,
    setEditQuantity,
    handleToggleSelect,
    handleOpenItemDetails,
    handleRemoveFromCart,
    handleToggleShare,
  } = props;

  const cartCtx = useWebDesignedCart();

  let filteredItems = cartItems;
  if (selectedSection === 'liked') {
    filteredItems = cartItems.filter((item) => item.isLiked);
  } else if (selectedSection === 'favorites') {
    filteredItems = cartItems.filter((item) => item.isFavorite);
  }

  if (filteredItems.length === 0) {
    return (
      <div className="text-center py-12">
        <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 font-medium text-lg mb-2">
          {selectedSection === 'liked' 
            ? t('shoppingCart.noLikedItems')
            : selectedSection === 'favorites'
            ? t('shoppingCart.noFavorites')
            : t('shoppingCart.cartEmpty')
          }
        </p>
        <p className="text-sm text-gray-400">
          {selectedSection === 'liked'
            ? t('shoppingCart.noLikedItemsDesc')
            : selectedSection === 'favorites'
            ? t('shoppingCart.noFavoritesDesc')
            : t('shoppingCart.cartEmptyDesc')
          }
        </p>
      </div>
    );
  }

  // The VUM forfait covers the cheapest accommodation only
  const accommodationItems = cartItems.filter((i) => i.type === 'accommodation');
  const vumCoveredItemId = vumPromo && accommodationItems.length > 0
    ? accommodationItems.reduce((min, i) => i.itemPrice < min.itemPrice ? i : min).id
    : null;

  return (
    <div className="space-y-4">
      {filteredItems.map((item) => {
      const details = getMyCartItemDetails(item);
      const Icon = details.icon;
      const unitTaxIncl = cartCatalogUnitPriceTaxIncl(item);
      const taxHeb = item.taxeHebergementEnabled !== false;

      // Only the cheapest accommodation is covered by the VUM forfait
      const isVumCovered = item.type === 'accommodation' && item.id === vumCoveredItemId;
      
      const isSelected = selectedItems.has(item.id);
      
      return (
        <div 
          key={item.id} 
          className={`border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow ${
            isSelected ? 'border-nature-500 ring-2 ring-nature-200' : 'border-gray-200'
          }`}
        >
          {/* Item Header */}
          <div className="p-4">
            <div className="flex items-start space-x-3">
              {/* Checkbox */}
              <button
                onClick={() => handleToggleSelect(item.id)}
                className="mt-1 flex-shrink-0"
                aria-label={isSelected ? t('common.deselect') : t('common.select')}
              >
                {isSelected ? (
                  <CheckSquare className="w-5 h-5 text-nature-600" />
                ) : (
                  <Square className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              <div className="flex items-start space-x-3 flex-1 min-w-0">
                <div className="bg-nature-100 p-2.5 rounded-lg flex-shrink-0">
                  <Icon className="w-6 h-6 text-nature-600" />
                </div>
                <div className="flex-1 min-w-0">
                  {editingItemId === item.id && item.type !== 'accommodation' ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {t('common.quantity') || 'Quantity'}
                        </label>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={editQuantity}
                          onChange={(e) => setEditQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-nature-500 focus:border-nature-500"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        {t('common.price')} {t('common.notEditable') || 'is not editable.'}
                      </p>
                    </div>
                  ) : (
                    // Normal mode - Show item details
                    <button
                      type="button"
                      onClick={() => handleOpenItemDetails(item)}
                      disabled={item.itemId.startsWith('vum-included-')}
                      className={`w-full text-left rounded-lg p-1 -m-1 transition-colors ${item.itemId.startsWith('vum-included-') ? 'cursor-default' : 'hover:bg-gray-50'}`}
                      title={item.itemId.startsWith('vum-included-') ? undefined : (t('common.viewDetails') || 'View details')}
                    >
                      <h4 className="font-semibold text-gray-900 text-base mb-1">{item.itemName}</h4>
                      <p className="text-sm text-gray-500 line-clamp-2 mb-2">{details.description}</p>
                      <div className="flex items-center justify-between">
                        {item.itemId.startsWith('vum-included-') ? (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">
                            🎟 Inclus VUM
                          </span>
                        ) : (
                          <div>
                            <p className="text-lg font-bold text-nature-600 tabular-nums">
                              ${unitTaxIncl !== null ? unitTaxIncl.toFixed(2) : item.itemPrice.toFixed(2)}
                              {item.type === 'accommodation'
                                ? ` ${t('common.perNightPerPerson')}`
                                : item.type === 'activity'
                                  ? ` ${t('common.perPerson')}`
                                  : ''}
                            </p>
                            <div className="mt-0.5 text-[11px] text-gray-400">{t('common.priceTaxIncludedShort')}</div>
                          </div>
                        )}
                      </div>
                      {/* Calculated price for selected items */}
                      {isSelected && shoppingCartData && (
                        <div className="mt-2 text-sm text-gray-600">
                          {item.type === 'accommodation' && calculatedTotals.duration > 0 && (
                            isVumCovered ? (
                              <div className="space-y-0.5">
                                {calculatedTotals.vumIncludedNights > 0 && (
                                  <div className="flex items-center gap-2">
                                    <span className="line-through text-gray-400 tabular-nums">
                                      {calculatedTotals.vumIncludedNights} nuit{calculatedTotals.vumIncludedNights > 1 ? 's' : ''} × $
                                      {clientCatalogPriceStayPerNight(item.itemPrice, taxHeb).toFixed(2)}
                                    </span>
                                    <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">Inclus</span>
                                  </div>
                                )}
                                {calculatedTotals.vumExtraNights > 0 ? (
                                  <span className="font-semibold text-nature-600 tabular-nums">
                                    +{calculatedTotals.vumExtraNights} nuit{calculatedTotals.vumExtraNights > 1 ? 's' : ''} supp. : $
                                    {clientCatalogStayLineTaxIncl(
                                      item.itemPrice,
                                      calculatedTotals.vumExtraNights,
                                      taxHeb,
                                    ).toFixed(2)}
                                  </span>
                                ) : (
                                  <span className="font-semibold text-emerald-600">$0 — Forfait inclus</span>
                                )}
                              </div>
                            ) : (
                              <span className="tabular-nums">
                                {calculatedTotals.duration}{' '}
                                {calculatedTotals.duration === 1 ? t('guestReservation.night') : t('guestReservation.nights')}:{' '}
                                <span className="font-semibold text-nature-600 ml-1">
                                  $
                                  {clientCatalogStayLineTaxIncl(item.itemPrice, calculatedTotals.duration, taxHeb).toFixed(
                                    2,
                                  )}
                                </span>
                              </span>
                            )
                          )}
                          {item.type === 'activity' && calculatedTotals.totalGuests > 1 && (
                            <span className="tabular-nums">
                              {calculatedTotals.totalGuests} {t('common.persons')}:{' '}
                              <span className="font-semibold text-nature-600 ml-1">
                                $
                                {clientCatalogGoodsLineTaxIncl(
                                  item.itemPrice * calculatedTotals.totalGuests * (item.quantity ?? 1),
                                ).toFixed(2)}
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {editingItemId === item.id && item.type !== 'accommodation' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        updateCartItem(item.id, { quantity: editQuantity });
                        setEditingItemId(null);
                      }}
                      className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors flex-shrink-0"
                      title={t('common.save') || 'Save'}
                    >
                      <Save className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingItemId(null)}
                      className="p-2 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors flex-shrink-0"
                      title={t('common.cancel') || 'Cancel'}
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        if (item.type === 'accommodation') {
                          cartCtx?.openBookingAssistant();
                          return;
                        }
                        setEditingItemId(item.id);
                        setEditQuantity(item.quantity ?? 1);
                      }}
                      className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 transition-colors flex-shrink-0"
                      title={
                        item.type === 'accommodation'
                          ? t('shoppingCart.configureWithAssistant')
                          : t('common.edit') || 'Adjust'
                      }
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        handleToggleShare(item.id);
                        if (navigator.share) {
                          navigator.share({
                            title: item.itemName,
                            text: details.description,
                            url: window.location.href
                          }).catch(() => {
                            navigator.clipboard.writeText(window.location.href);
                            alert(t('common.linkCopied'));
                          });
                        } else {
                          navigator.clipboard.writeText(window.location.href);
                          alert(t('common.linkCopied'));
                        }
                      }}
                      className="p-2 rounded-lg bg-white text-gray-400 hover:bg-gray-100 border border-gray-200 transition-colors"
                      title={t('common.share')}
                    >
                      <Share2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleRemoveFromCart(item.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors p-2 rounded-lg flex-shrink-0"
                      title={t('common.remove')}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    })}
    </div>
  );
};

export default MyCartSidebarLineItems;
